import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI } from '../../utils/api';
import AdminHeader from './AdminHeader';

/**
 * Pipeline health for admins: where applications sit, how long they have sat
 * there, what is coming in, and what converts. Read-only; numbers come from
 * GET /api/jobs/pipeline/health.
 */
const STAGE_ORDER = ['new', 'reviewing', 'shortlisted', 'questionnaire_sent', 'questionnaire_completed', 'screening_call', 'screening_passed', 'assessment_sent', 'assessment_completed', 'interview', 'offer', 'accepted', 'hired', 'declined', 'rejected', 'withdrawn', 'archived'];

const tile = { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1rem 1.1rem' };
const num = { fontSize: '1.75rem', fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 };
const lbl = { fontSize: '0.8rem', color: '#6B7280', marginTop: '0.25rem' };
const th = { textAlign: 'left', fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', padding: '0.5rem 0.6rem', borderBottom: '1px solid #E5E7EB' };
const td = { padding: '0.5rem 0.6rem', borderBottom: '1px solid #F3F4F6', fontSize: '0.9rem', color: '#111827' };
const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const PipelineHealth = ({ currentView, onNavigate }) => {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    jobsAPI.getPipelineHealth()
      .then(res => setHealth(res.data.health))
      .catch(err => setError(err.response?.data?.error || 'Failed to load pipeline health'));
  }, []);

  const go = onNavigate || ((view) => navigate(`/admin/${view}`));

  return (
    <div className="system-analytics">
      <AdminHeader currentView={currentView || 'pipeline'} onNavigate={go} />
      <div className="system-analytics-content" style={{ padding: '1.5rem' }}>
        <div className="analytics-header" style={{ marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Pipeline Health</h2>
          {health && <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>as of {new Date(health.generatedAt).toLocaleString()}</span>}
        </div>

        {error && <div className="error-banner"><span>{error}</span></div>}
        {!health && !error && <p style={{ color: '#6B7280' }}>Loading...</p>}

        {health && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={tile}><div style={num}>{health.totals.applications}</div><div style={lbl}>applications, {health.totals.activeJobs} active jobs</div></div>
              <div style={tile}><div style={num}>{health.queues.ready}</div><div style={lbl}>ready to review</div></div>
              <div style={tile}><div style={num}>{health.queues.waiting}</div><div style={lbl}>waiting on candidate</div></div>
              <div style={tile}><div style={{ ...num, color: health.queues.stale ? '#B45309' : '#111827' }}>{health.queues.stale}</div><div style={lbl}>stale ({health.staleDays}+ days idle)</div></div>
              <div style={tile}><div style={num}>{health.medianDaysInStage.new ?? 0}d</div><div style={lbl}>median idle in new</div></div>
              <div style={tile}><div style={num}>{health.conversion.interviewRate}%</div><div style={lbl}>reach interview ({health.conversion.reachedInterview})</div></div>
              <div style={tile}><div style={num}>{health.conversion.hired}</div><div style={lbl}>hired ({health.conversion.hireRate}%)</div></div>
            </div>

            <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Intake, last 8 weeks</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '90px', marginBottom: '1.5rem', ...tile }}>
              {(() => { const max = Math.max(1, ...health.intakeByWeek.map(w => w.count)); return health.intakeByWeek.map(w => (
                <div key={w.weekStart} title={`${w.weekStart}: ${w.count}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                  <div style={{ fontSize: '0.7rem', color: '#374151', marginBottom: '2px' }}>{w.count}</div>
                  <div style={{ width: '100%', background: '#1E40AF', height: `${Math.max(2, (w.count / max) * 60)}px`, borderRadius: '2px 2px 0 0' }}></div>
                </div>
              )); })()}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div style={{ ...tile, overflowX: 'auto' }}>
                <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Per job</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>Job</th><th style={{ ...th, textAlign: 'right' }}>Ready</th><th style={{ ...th, textAlign: 'right' }}>Waiting</th><th style={{ ...th, textAlign: 'right' }}>Stale</th><th style={{ ...th, textAlign: 'right' }}>Closed</th><th style={{ ...th, textAlign: 'right' }}>Total</th></tr></thead>
                  <tbody>
                    {health.jobs.map(j => (
                      <tr key={j.jobId} style={{ opacity: j.status === 'active' ? 1 : 0.6 }}>
                        <td style={td}>{j.title} <span style={{ color: '#6B7280', fontSize: '0.75rem' }}>{j.status}</span></td>
                        <td style={tdNum}>{j.ready}</td><td style={tdNum}>{j.waiting}</td><td style={{ ...tdNum, color: j.stale ? '#B45309' : '#111827' }}>{j.stale}</td><td style={tdNum}>{j.closed}</td><td style={tdNum}>{j.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ ...tile, overflowX: 'auto' }}>
                <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>By stage</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>Stage</th><th style={{ ...th, textAlign: 'right' }}>Applications</th><th style={{ ...th, textAlign: 'right' }}>Median days idle</th></tr></thead>
                  <tbody>
                    {STAGE_ORDER.filter(s => health.totals.byStatus[s]).map(s => (
                      <tr key={s}><td style={td}>{s.replace(/_/g, ' ')}</td><td style={tdNum}>{health.totals.byStatus[s]}</td><td style={tdNum}>{health.medianDaysInStage[s] ?? 0}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PipelineHealth;
